import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Clock, Youtube, Video } from "lucide-react";
import { useState, useEffect } from "react";
import { getStorageUrl, STORAGE_BUCKETS } from "@/utils/storageUtils";

interface EpisodeCardProps {
  title: string;
  description: string;
  thumbnail: string;
  duration: string;
  youtubeId?: string;
  vimeoId?: string;
  videoPlatform?: "youtube" | "vimeo";
  onPlay: (youtubeId?: string, vimeoId?: string, videoPlatform?: "youtube" | "vimeo") => void;
}

const EpisodeCard = ({ 
  title, 
  description, 
  thumbnail, 
  duration, 
  youtubeId, 
  vimeoId, 
  videoPlatform,
  onPlay 
}: EpisodeCardProps) => {
  const [vimeoThumbnail, setVimeoThumbnail] = useState<string | null>(null);

  // Determine which video to show based on platform or available IDs
  const getVideoConfig = () => {
    if (videoPlatform === "vimeo" && vimeoId) {
      return { type: "vimeo", id: vimeoId, icon: Video };
    }
    if (videoPlatform === "youtube" && youtubeId) {
      return { type: "youtube", id: youtubeId, icon: Youtube };
    }
    // Fallback logic
    if (vimeoId) {
      return { type: "vimeo", id: vimeoId, icon: Video };
    }
    if (youtubeId) {
      return { type: "youtube", id: youtubeId, icon: Youtube };
    }
    return null;
  };

  const videoConfig = getVideoConfig();

  // Fetch Vimeo thumbnail only if no uploaded thumbnail is available
  useEffect(() => {
    if (!thumbnail && videoConfig?.type === "vimeo" && videoConfig.id) {
      const fetchVimeoThumbnail = async () => {
        try {
          const response = await fetch(
            `https://vimeo.com/api/v2/video/${videoConfig.id}.json`
          );
          if (response.ok) {
            const data = await response.json();
            if (data && data[0] && data[0].thumbnail_medium) {
              setVimeoThumbnail(data[0].thumbnail_medium);
            }
          }
        } catch (error) {
          console.error("Failed to fetch Vimeo thumbnail:", error);
        }
      };

      fetchVimeoThumbnail();
    }
  }, [thumbnail, videoConfig]);

  const getThumbnailUrl = () => {
    // Priority 1: Use uploaded thumbnail if available
    if (thumbnail) {
      // If it's a storage path, construct the URL; otherwise use as-is (for external URLs)
      if (!thumbnail.startsWith('http')) {
        return getStorageUrl(STORAGE_BUCKETS.EPISODE_THUMBNAILS, thumbnail);
      }
      return thumbnail;
    }
    
    // Priority 2: Use video source thumbnail as fallback
    if (videoConfig?.type === "youtube") {
      return `https://img.youtube.com/vi/${videoConfig.id}/mqdefault.jpg`;
    }
    if (videoConfig?.type === "vimeo") {
      return vimeoThumbnail || "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=500&h=300&fit=crop";
    }
    
    // Priority 3: Default fallback image
    return "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=500&h=300&fit=crop";
  };

  if (!videoConfig) {
    return (
      <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-2 border-sky-blue/20 opacity-50">
        <div className="relative">
          <img 
            src={getThumbnailUrl()}
            alt={`${title} - Bobo & Teedee® episode thumbnail`}
            className="w-full h-48 object-cover"
          />
          <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-sm flex items-center">
            <Clock className="mr-1 h-3 w-3" />
            {duration}
          </div>
        </div>
        
        <CardContent className="p-6">
          <h3 className="text-xl font-bold text-teedee-blue mb-3">
            {title}
          </h3>
          <p className="text-gray-600 mb-4 line-clamp-3">
            {description}
          </p>
          <div className="text-center text-gray-500 italic">
            Video not available
          </div>
        </CardContent>
      </Card>
    );
  }

  const IconComponent = videoConfig.icon;

  return (
    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-2 border-sky-blue/20">
      <div className="relative">
        <img 
          src={getThumbnailUrl()}
          alt={`${title} - Bobo & Teedee® episode thumbnail`}
          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <Button 
            onClick={() => onPlay(youtubeId, vimeoId, videoPlatform)}
            size="lg"
            className="bg-teedee-blue hover:bg-teedee-blue/90 text-white rounded-full shadow-lg"
          >
            <Play className="mr-2 h-5 w-5" />
            Watch Now
          </Button>
        </div>
        <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-sm flex items-center">
          <Clock className="mr-1 h-3 w-3" />
          {duration}
        </div>
        <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-sm flex items-center">
          <IconComponent className="mr-1 h-3 w-3" />
          {videoConfig.type}
        </div>
      </div>
      
      <CardContent className="p-6">
        <h3 className="text-xl font-bold text-teedee-blue mb-3 group-hover:text-bobo-green transition-colors">
          {title}
        </h3>
        <p className="text-gray-600 mb-4 line-clamp-3">
          {description}
        </p>
        <Button 
          onClick={() => onPlay(youtubeId, vimeoId, videoPlatform)}
          variant="outline" 
          className="w-full border-teedee-blue text-teedee-blue hover:bg-teedee-blue hover:text-white transition-all duration-300"
        >
          <Play className="mr-2 h-4 w-4" />
          Play Episode
        </Button>
      </CardContent>
    </Card>
  );
};

export default EpisodeCard;
